import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WalletPass } from "../components/WalletPass";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight } from "lucide-react";

// Category-smart default palettes. Each merchant's first pass reads as THEIR
// brand from second one, not as another BackPocket-green/black clone. Cafes
// get a warm coffee brown, gyms a deep navy, salons a dusty rose, and so on.
// Owners can override at the "Design your pass" step if they want.
const CATEGORY_PALETTES = {
  Cafe:       { brand_color: "#3B1F0F", secondary_color: "#E9C46A", text_color: "#FFFFFF", reward_hint: "Free coffee" },
  Barber:     { brand_color: "#141518", secondary_color: "#D97757", text_color: "#FFFFFF", reward_hint: "Free cut" },
  Gym:        { brand_color: "#0F1E33", secondary_color: "#7BE495", text_color: "#FFFFFF", reward_hint: "Free session" },
  Salon:      { brand_color: "#2A1522", secondary_color: "#FFB4C8", text_color: "#FFFFFF", reward_hint: "Free blow-dry" },
  Restaurant: { brand_color: "#3A0E15", secondary_color: "#F2C97D", text_color: "#FFFFFF", reward_hint: "$20 off" },
  Retail:     { brand_color: "#1A2233", secondary_color: "#F4B942", text_color: "#FFFFFF", reward_hint: "10% off" },
  Other:      { brand_color: "#1D1F25", secondary_color: "#E7BE7B", text_color: "#FFFFFF", reward_hint: "Free reward" },
};

export default function Onboarding() {
  const { refresh, switchOrg } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [biz, setBiz] = useState({ name: "", category: "Cafe", website: "", country: "AU", currency: "AUD", contact_email: "" });
  // Program state seeded from the "Cafe" palette so the very first pass render
  // reads as a real business's pass rather than a BackPocket brand-color clone.
  const initialPalette = CATEGORY_PALETTES.Cafe;
  const [prog, setProg] = useState({
    name: "Loyalty Program", type: "visit", pass_title: "Loyalty Card",
    brand_color: initialPalette.brand_color,
    secondary_color: initialPalette.secondary_color,
    text_color: initialPalette.text_color,
    logo_url: "",
    reward: { name: initialPalette.reward_hint, threshold: 10, trigger_type: "visits", resets_after_redeem: true },
  });
  const [launched, setLaunched] = useState(null); // { org, program, joinUrl }

  // When the merchant picks a business category, re-seed the pass palette so
  // the preview instantly looks tuned to their industry. Only touches
  // brand/accent/text + the reward NAME if they haven't customised those yet.
  const applyCategory = (categoryId) => {
    const pal = CATEGORY_PALETTES[categoryId] || CATEGORY_PALETTES.Other;
    setBiz((b) => ({ ...b, category: categoryId }));
    setProg((p) => {
      const looksDefault = Object.values(CATEGORY_PALETTES).some((c) => c.brand_color === p.brand_color && c.secondary_color === p.secondary_color);
      if (!looksDefault) return p; // owner has customised — do NOT overwrite
      return {
        ...p,
        brand_color: pal.brand_color,
        secondary_color: pal.secondary_color,
        text_color: pal.text_color,
        reward: { ...p.reward, name: pal.reward_hint },
      };
    });
  };

  const preview = { ...prog, org_name: biz.name || "Your Business" };

  // Two-step onboarding: (1) Business → (2) Plan + card in Stripe Checkout.
  // Step 1 creates the org so we have an org_id to attach a Stripe customer
  // and subscription to. Step 2 lives at /onboarding/plan and hands the
  // merchant to Stripe. The dashboard is gated behind subscription.stripe_customer_id
  // so users can't skip payment by navigating to /app directly.
  const launch = async () => {
    setBusy(true);
    try {
      let picked_plan = null;
      try { picked_plan = localStorage.getItem("bp_signup_plan"); } catch (_) {}
      const chosen = picked_plan || "growth";
      const { data: org } = await api.post("/orgs", { ...biz, currency: biz.country === "NZ" ? "NZD" : "AUD", plan_id: chosen });
      await api.post(`/orgs/${org.id}/locations`, { name: "Main location", country: biz.country });
      await refresh();
      switchOrg({ ...org, role: "owner" });
      try { localStorage.setItem("bp_show_tour", "1"); } catch (_) {}
      window.location.assign("/app");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F1E9] flex flex-col">
      <div className="flex items-center gap-2.5 p-6"><BrandMark size={28} /><span className="font-display font-bold">BackPocket</span></div>

      <div className="flex-1 grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto w-full px-6 pb-12">
        <div>
          <div data-testid="onb-step-1">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/50 mb-3">Step 1 of 2 — Business</div>
            <h1 className="font-display font-extrabold text-[clamp(2.2rem,4vw,3.2rem)] leading-[1.02] tracking-[-0.02em]">Tell us about your business.</h1>
            <p className="text-[#25282E]/75 mt-3.5 mb-8 text-[15px] leading-relaxed max-w-md">Takes 20 seconds. Next you'll pick a plan and add your card — then your dashboard is ready.</p>
            <div className="space-y-4 max-w-md">
              <div><Label>Business name</Label><Input data-testid="onb-name" className="mt-1.5 bg-white h-11 rounded-xl" value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} placeholder="Pocket Coffee Co." autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <select className="mt-1.5 w-full h-11 rounded-xl border border-[#E8E5DE] bg-white px-3 text-sm" value={biz.category} onChange={(e) => applyCategory(e.target.value)} data-testid="onb-category">
                    {["Cafe", "Barber", "Gym", "Salon", "Restaurant", "Retail", "Other"].map((c) => <option key={c}>{c}</option>)}
                  </select></div>
                <div><Label>Country</Label>
                  <select data-testid="onb-country" className="mt-1.5 w-full h-11 rounded-xl border border-[#E8E5DE] bg-white px-3 text-sm" value={biz.country} onChange={(e) => setBiz({ ...biz, country: e.target.value })}>
                    <option value="AU">Australia (AUD)</option><option value="NZ">New Zealand (NZD)</option>
                  </select></div>
              </div>
            </div>
            <Button data-testid="onb-launch" disabled={!biz.name || busy} onClick={launch}
              className="mt-8 btn-lift bg-[#0B0D10] text-white rounded-full h-12 px-7 font-semibold text-[15px]">
              {busy ? "Setting things up…" : <>Next: pick a plan <ArrowRight size={16} className="ml-2" /></>}
            </Button>
            <p className="text-[12px] text-[#3a3d44]/50 mt-4">30-day free trial · Card required · Cancel any time · No charge until day 31</p>
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center">
          <div className="relative">
            <div className="absolute -inset-10 rounded-[36px] bg-gradient-to-tr from-[#6474FF]/12 to-[#C8FF4D]/20 blur-3xl" />
            <div className="relative">
              <WalletPass program={preview} wallet="apple" state={{ balance: 6, pass_number: "BP-00042" }} />
              <div className="mt-4 text-center text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/45">A preview of your first pass</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

